// src\app\components\CheatSheetPdf_Person.js

import pdfMake from "pdfmake/build/pdfmake.js"
import pdfFonts from "pdfmake/build/vfs_fonts.js"
import nLogo from "../assets/images/numerable-logo-2022-512w-8b.png"
import cLogo from "../assets/images/the350club-logo-2024-8b.png"
import HOOK from "./hooks"
import { CHECKBOX } from "../modules/checkbox_constants"

import { COLOR, COLUMN, FORMATTING, KIND, TABLE_BIT, TENSE_ENUM } from "../modules/constants"
import COLUMNS from "../modules/columns"
import { getCapacities, getNameProp } from "../modules/utilities"
import { docDefinition, footer, get, getHeader, header, infoTable, initTableLayouts, rolesLabel, sortArray, styles, width } from "./CheatSheetPdf_Common"
import { detailsTable, titleTable } from "./CheatSheetPdf_Common"

pdfMake.vfs = pdfFonts

const { center, right } = FORMATTING
const { greyText, veryDarkGreyText } = COLOR.PDF
const tableLayouts = {}

const cheatSheetPdf_PersonCommittees = props => {
  // console.log("cheatSheetPdf_PersonCommittees props:", props)
  initTableLayouts(props.brandColor, tableLayouts)
  const document = createDocument({ ...props, tableLayouts })
  pdfMake.createPdf(document).open()
}

const createDocument = props => ({
  ...docDefinition,
  styles: styles(props),
  header: header(props),
  content: getContent(props),
  images: {
    numerableLogo: nLogo,
    the350ClubLogo: cLogo,
  },
  footer: (currentPage, pageCount) => footer(currentPage, pageCount),
})

const getContent = props => {
  // console.log("getContent props: ", props)
  props.untilColumn = !!(props.orderSet & TABLE_BIT.RESIGNED)
  const content = [
    titleTable(props),
    detailsTable(props),
    rolesLabel({ ...props, tableLayouts, committees: true }),
  ]

  const nodes = props.nodes.filter(i => i.level > 0) // all but the hub node
  // console.log('nodes:', nodes)
  getCommitteesContent({ ...props, content, nodes, tenseEnum: TENSE_ENUM.CURRENT })

  content.push(infoTable({ ...props, tableLayouts }))

  return content
}

const getCommitteesContent = props => {
  const { content, tenseEnum } = props
  content.push(committeesTable(bodyTableProps(props, tenseEnum)))
}

const bodyTableProps = (props, tenseEnum) => ({
  ...props,
  tenseBits: (CHECKBOX[HOOK.TENSE][tenseEnum].BIT & props.checkboxes[HOOK.TENSE]),
  tenseEnum,
})

const committeesTable = props => {
  // console.log('committeesTable props:', props)
  const body = getCommittees(props)

  return body
    ? ({
      style: "bodyTable",
      layout: tableLayouts.committees,
      table: {
        widths: ["*", "*", "*", width.committee, width.committee, width.committee, width.committee, width.committee, width.committee],
        headerRows: 1,
        body,
      },
    })
    : null
}

const getHeaders = () => {
  const columns = COLUMNS.LANDSCAPE[KIND.NEXUS.PERSON.COMMITTEES]
  const style = "tableHeader"
  const cell = {}

  getHeader({ cell, sortId: COLUMN.COMPANY, style })
  getHeader({ cell, sortId: COLUMN.ROLE, style })
  getHeader({ cell, sortId: COLUMN.TENURE, style, alignment: right })

  getHeader({ cell, sortId: COLUMN.COMMITTEE.AUDIT.CHAIRS, style, alignment: center })
  getHeader({ cell, sortId: COLUMN.COMMITTEE.AUDIT.MEMBER, style, alignment: center })

  getHeader({ cell, sortId: COLUMN.COMMITTEE.REMUNERATION.CHAIRS, style, alignment: center })
  getHeader({ cell, sortId: COLUMN.COMMITTEE.REMUNERATION.MEMBER, style, alignment: center })

  getHeader({ cell, sortId: COLUMN.COMMITTEE.NOMINATION.CHAIRS, style, alignment: center })
  getHeader({ cell, sortId: COLUMN.COMMITTEE.NOMINATION.MEMBER, style, alignment: center })

  // console.log('cell:',cell)
  // console.log('columns:',columns)

  const array = []
  for (const [key, value] of Object.entries(columns))
    if (value === true && cell[parseInt(key)])
      array.push(cell[parseInt(key)])

  return array
}

const getCommittees = props => {
  // console.log("getCommittees props:", props)
  const { connections, features, orderSet, tableOrder } = props
  const array = []

  props.nodes.forEach(node => {
    // Get all connections where parentId === node.id
    // NB: The connections have already been filtered by childId === hubNode id
    const { entity } = node
    const { cmtes } = entity.companyProps.lse
    const indexBit = get.indexBit(node)
    // Handle legacy entity structure
    const { name } = entity.commonProps ? entity.commonProps : entity

    const connection = Object.filter(connections, c => c.parentId === node.id)
    const capacities = getCapacities(connection, node.id)
    const officerCapacities = (capacities && capacities.officer) || []
    const bold = indexBit ? true : null
    const color = greyText
    const alignment = center

    officerCapacities.forEach(role => {
      // console.log('role:', role)
      const row = [
        { text: getNameProp.toPresent(name), color: veryDarkGreyText, sortValue: getNameProp.toSort(name), bold },
        {
          text:
            features.EnablePosition && role.position
              ? role.position.title
              : role.roleDescription
                ? role.roleDescription : "Officer",
          color: greyText,
          sortValue: get.positionSortValue(role.position.title),
          bold
        },
        { text: get.yearsAndMonths(role.from, role.until, right) },
      ]

      // Where cmtes[x][0] === -1, there is no committee
      rowPush({ alignment, bold, cmte: role.cmtes.aud, color, row, noCommittee: cmtes[0][0] === -1 })
      rowPush({ alignment, bold, cmte: role.cmtes.rem, color, row, noCommittee: cmtes[1][0] === -1 })
      rowPush({ alignment, bold, cmte: role.cmtes.nom, color, row, noCommittee: cmtes[2][0] === -1 })

      array.push(row)
    })
  })

  if (array.length) {
    const headers = getHeaders()
    sortArray({ array, headers, orderSet, tableOrder, kind: KIND.NEXUS.PERSON.COMMITTEES })
    return [headers, ...array]
  } else
    return null
}

const rowPush = ({ alignment, bold, cmte, color, row, noCommittee }) => {
  const style = noCommittee ? "noCommittee" : null
  row.push({ text: get.committeeObj(cmte === 1, alignment), color, sortValue: cmte === 1 ? 1 : 2, bold, style })
  row.push({ text: get.committeeObj(cmte === 2, alignment), color, sortValue: cmte === 2 ? 1 : 2, bold, style })
}

export default cheatSheetPdf_PersonCommittees
